import { CheckCircle2, Loader } from 'lucide-react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatTile } from '@/components/ui/stat-tile';
import { useStudentProgress } from '@/features/memorization/hooks';
import { useMyStudentProfile } from '@/features/students/hooks';
import { useHasCapability } from '@/lib/capabilities';

/**
 * "Ringkasan Saya" (Phase G-4, Student Experience). Renders only when the
 * authenticated account resolves to a real Student profile in the active
 * organization (`useMyStudentProfile`) - a Teacher/Admin/Parent-only
 * account gets nothing here, not an empty shell. The preview reuses the
 * exact same `GET /memorization/progress/:studentId` numbers
 * `ProgressSection` renders on the shared Student workspace - no
 * alternative metric, no fabricated "streak" or "next action" - and links
 * into that same shared workspace (`/students/:id`) rather than
 * duplicating it.
 */
export function MyStudentSummaryCard(): JSX.Element | null {
  const canManageStudents = useHasCapability('students:manage');
  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
  } = useMyStudentProfile(!canManageStudents);
  const { data: progress, isLoading: progressLoading } = useStudentProgress(profile?.id ?? '');

  if (canManageStudents) {
    return null;
  }

  if (profileLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ringkasan Saya</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (profileError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ringkasan Saya</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">Gagal memuat ringkasan Anda.</p>
        </CardContent>
      </Card>
    );
  }

  // A resolved-but-empty result (`null`) is a valid outcome, not an error -
  // an account with no Student profile of its own in this organization
  // (a Parent-only caller, for example) simply gets no card.
  if (profile === null || profile === undefined) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ringkasan Saya</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {progressLoading && (
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        )}

        {!progressLoading && progress !== undefined && (
          <div className="grid grid-cols-2 gap-3">
            <StatTile
              label="Target Berjalan"
              value={String(progress.targetsInProgressCount)}
              icon={Loader}
            />
            <StatTile
              label="Target Selesai"
              value={String(progress.targetsCompletedCount)}
              icon={CheckCircle2}
              celebrate={progress.targetsCompletedCount > 0}
            />
          </div>
        )}

        {/* Points at the reader's own hafalan rather than at their row in
            the roster - the management workspace is where an ustadz reads
            about them, not where they read about themselves. */}
        <Link to="/hafalan" className="text-sm font-medium text-primary hover:underline">
          Buka hafalan saya
        </Link>
      </CardContent>
    </Card>
  );
}
